import React from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Check, Star } from 'lucide-react';
import { useToast } from './ui/use-toast';

export const Pricing = () => {
  const { toast } = useToast();

  const handlePlanClick = (planName) => {
    toast({
      title: "🎯 Abonnement bientôt disponible",
      description: `Le plan ${planName} sera disponible très prochainement. Contactez-nous pour plus d'informations !`,
      duration: 5000,
    });
  };

  const plans = [
    {
      name: 'Standard',
      price: '7,000',
      description: 'Pour entrepreneurs débutant ou testant le marché digital',
      badge: null,
      features: [
        { text: 'Catalogue illimité', included: true },
        { text: 'Dashboard basique', included: true },
        { text: 'Support prioritaire email/chat', included: true },
        { text: 'Réduction commission -1%', included: true },
        { text: 'Accès promotions exclusives', included: true },
        { text: 'Webinaires & formations', included: false },
        { text: 'Gestionnaire dédié 24/7', included: false },
        { text: 'Rapports stratégiques', included: false },
      ],
      cta: 'Commencer',
      popular: false,
      stars: 2,
    },
    {
      name: 'Médium',
      price: '12,000',
      description: 'Pour entreprises établies cherchant visibilité et croissance',
      badge: 'Populaire',
      features: [
        { text: 'Publications illimitées', included: true },
        { text: '3 produits en vedette/mois', included: true },
        { text: 'Dashboard avancé temps réel', included: true },
        { text: 'Support chat & email prioritaire', included: true },
        { text: 'Réduction commission -1.25%', included: true },
        { text: 'Webinaires & formations avancées', included: true },
        { text: 'Campagnes marketing gratuites', included: true },
        { text: 'Rapports mensuels', included: false },
      ],
      cta: 'Choisir Médium',
      popular: true,
      stars: 4,
    },
    {
      name: 'Premium',
      price: '17,000',
      description: 'Pour leaders du marché et marques ambitieuses',
      badge: 'Recommandé',
      features: [
        { text: 'Visibilité maximale homepage', included: true },
        { text: 'Dashboard Premium + Analytics', included: true },
        { text: 'Gestionnaire dédié 24/7 multicanal', included: true },
        { text: 'Réduction commission -1.75%', included: true },
        { text: 'Participation prioritaire campagnes', included: true },
        { text: 'Formations exclusives + coaching', included: true },
        { text: 'Réseau partenaires', included: true },
        { text: 'Rapports mensuels détaillés', included: true },
      ],
      cta: 'Passer Premium',
      popular: false,
      stars: 5,
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-muted to-background">
      <div className="container">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-primary mb-3 uppercase tracking-wider">Tarifs</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Choisissez Votre <span className="text-primary">Formule</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Des plans conçus pour accompagner votre croissance à chaque étape
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) => (
            <Card
              key={index}
              className={`relative overflow-hidden flex flex-col ${
                plan.popular
                  ? 'border-primary shadow-glow scale-105 lg:scale-110'
                  : 'border-border hover-lift'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-semibold px-4 py-1 rounded-bl-lg">
                  {plan.badge}
                </div>
              )}

              <CardHeader className="text-center pb-8">
                <div className="mb-4">
                  <h3 className="text-2xl font-display font-bold text-foreground mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-center justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < plan.stars ? 'fill-primary text-primary' : 'text-muted'}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-4xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-muted-foreground">FCFA</span>
                  </div>
                  <span className="text-sm text-muted-foreground">par mois</span>
                </div>

                <Button
                  size="lg"
                  className={`w-full ${
                    plan.popular
                      ? 'bg-primary text-primary-foreground hover:bg-primary-glow shadow-glow'
                      : 'bg-secondary text-secondary-foreground hover:bg-secondary-light'
                  }`}
                  onClick={() => handlePlanClick(plan.name)}
                >
                  {plan.cta}
                </Button>
              </CardHeader>

              <CardContent className="flex-1">
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4">
                    Fonctionnalités incluses
                  </h4>
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div
                        className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                          feature.included
                            ? 'bg-primary/10'
                            : 'bg-muted'
                        }`}
                      >
                        {feature.included ? (
                          <Check size={14} className="text-primary" />
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </div>
                      <span
                        className={feature.included ? 'text-foreground' : 'text-muted-foreground line-through'}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-muted-foreground">
            Besoin d'une solution personnalisée ?{' '}
            <a href="#contact" className="text-primary font-semibold hover:underline">
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};